import React, { useState, useEffect, useRef, useContext } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar, Badge, Alert, Modal } from 'react-bootstrap';
import { MicOutline, CheckmarkCircleOutline, CloseCircleOutline, PlayOutline, StopOutline, RefreshOutline, LockClosedOutline } from 'react-ionicons';
import { useParameters } from '../Generals';
import GeneralContext from '../utils/generalContext';

export const PlacementTest = () => {
    const { user, global } = useContext(GeneralContext);
    const [params] = useParameters();

    const [lessonsData, setLessonsData] = useState([]);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [interactionLog, setInteractionLog] = useState([]);
    const [lessonErrors, setLessonErrors] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);
    const [assignedLevel, setAssignedLevel] = useState('');
    const [stuckLesson, setStuckLesson] = useState('');
    const [loading, setLoading] = useState(true);

    // Pronunciation states
    const [isListening, setIsListening] = useState(false);
    const [spokenText, setSpokenText] = useState('');
    const [pronunciationScore, setPronunciationScore] = useState(null);
    const [wordAccuracy, setWordAccuracy] = useState([]);
    const recognitionRef = useRef(null);

    // Anti-Copy, Anti-Paste & Anti-Selection Security Handlers
    const preventCopyPaste = (e) => {
        e.preventDefault();
        alert("🔒 Por motivos de seguridad y validez de la evaluación, copiar o pegar texto está deshabilitado.");
    };

    const securityProps = {
        onCopy: preventCopyPaste,
        onCut: preventCopyPaste,
        onPaste: preventCopyPaste,
        onContextMenu: (e) => e.preventDefault(),
        style: {
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            userSelect: 'none'
        }
    };

    useEffect(() => {
        fetchPlacementQuestions();
    }, []);

    const fetchPlacementQuestions = async () => {
        setLoading(true);
        try {
            const url = `${global.domain}v3-api/v3/placement_questions.json`;
            const response = await fetch(url);
            const data = await response.json();
            if (data && data.lessons) {
                setLessonsData(data.lessons);
            }
        } catch (error) {
            setLessonsData([
                {
                    lessonId: 1,
                    lessonTitle: 'Lección 1: Personal Pronouns & Greetings',
                    description: 'Evaluación de pronombres y saludos básicos.',
                    items: [
                        {
                            id: 101,
                            type: 'grammar',
                            question: 'Select the correct pronoun: ___ am a student at Birmingham Institute.',
                            options: ['He', 'I', 'They', 'She'],
                            correctAnswer: 'I'
                        },
                        {
                            id: 102,
                            type: 'vocabulary',
                            question: 'Which greeting is used when meeting someone for the first time in a formal context?',
                            options: ['Nice to meet you', 'See ya', 'What\'s up?', 'Bye'],
                            correctAnswer: 'Nice to meet you'
                        },
                        {
                            id: 103,
                            type: 'pronunciation',
                            question: 'Lee y pronuncia la siguiente frase en voz alta:',
                            targetPhrase: 'Hello, my name is Alex and I am glad to meet you.',
                            phoneticGuide: 'he-LOH, my naym iz AL-eks and eye am glad too meet yoo'
                        }
                    ]
                },
                {
                    lessonId: 2,
                    lessonTitle: 'Lección 2: Present Simple & Routines',
                    description: 'Evaluación de presente simple y terceras personas.',
                    items: [
                        {
                            id: 201,
                            type: 'grammar',
                            question: 'Choose the correct form: She ___ English every morning.',
                            options: ['study', 'studies', 'studying', 'is study'],
                            correctAnswer: 'studies'
                        },
                        {
                            id: 202,
                            type: 'grammar',
                            question: 'Which auxiliary verb is used for negative sentences in third person singular?',
                            options: ['don\'t', 'doesn\'t', 'isn\'t', 'aren\'t'],
                            correctAnswer: 'doesn\'t'
                        },
                        {
                            id: 203,
                            type: 'pronunciation',
                            question: 'Pronuncia la rutina diaria en voz alta:',
                            targetPhrase: 'She works at Birmingham Institute every day.',
                            phoneticGuide: 'shee werks at ber-ming-ham in-stih-toot ev-ree day'
                        }
                    ]
                },
                {
                    lessonId: 3,
                    lessonTitle: 'Lección 3: Possessive Adjectives & Prepositions',
                    description: 'Uso de posesivos y preposiciones de lugar.',
                    items: [
                        {
                            id: 301,
                            type: 'grammar',
                            question: 'Complete: John left ___ keys on the kitchen table.',
                            options: ['his', 'her', 'their', 'its'],
                            correctAnswer: 'his'
                        },
                        {
                            id: 302,
                            type: 'grammar',
                            question: 'Select the correct preposition: The book is ___ the shelf.',
                            options: ['on', 'in', 'at', 'underneath of'],
                            correctAnswer: 'on'
                        },
                        {
                            id: 303,
                            type: 'pronunciation',
                            question: 'Lee y pronuncia con entonación adecuada:',
                            targetPhrase: 'Our new classroom is on the second floor.',
                            phoneticGuide: 'ow-er noo klas-room iz on thah seh-kund flor'
                        }
                    ]
                },
                {
                    lessonId: 4,
                    lessonTitle: 'Lección 4: Reading Comprehension & Past Simple',
                    description: 'Lectura de comprensión y verbos en pasado.',
                    items: [
                        {
                            id: 401,
                            type: 'reading',
                            readingTitle: 'A Memorable Trip to Birmingham',
                            readingText: 'Last summer, Maria visited Birmingham to take an intensive English course. She stayed with a local host family who welcomed her warmly. Every afternoon after class, Maria walked through the city center, practiced her conversation skills with local residents, and explored historic landmarks. She said that speaking English every day boosted her confidence significantly.',
                            question: 'Why did Maria travel to Birmingham last summer?',
                            options: ['To take an intensive English course', 'To work as a teacher', 'To buy a house', 'To go on a business trip'],
                            correctAnswer: 'To take an intensive English course'
                        },
                        {
                            id: 402,
                            type: 'grammar',
                            question: 'What is the past simple form of the verb "go"?',
                            options: ['goed', 'went', 'gone', 'going'],
                            correctAnswer: 'went'
                        },
                        {
                            id: 403,
                            type: 'pronunciation',
                            question: 'Pronuncia el fragmento en pasado:',
                            targetPhrase: 'Yesterday I traveled to Birmingham and learned many useful words.',
                            phoneticGuide: 'yes-ter-day eye trav-uld too ber-ming-ham and lernd meh-nee yoos-ful werds'
                        }
                    ]
                },
                {
                    lessonId: 5,
                    lessonTitle: 'Lección 5: Modal Verbs & Future with Going To',
                    description: 'Modales y estructuras de futuro.',
                    items: [
                        {
                            id: 501,
                            type: 'grammar',
                            question: 'Complete: You ___ study hard if you want to pass the certificate.',
                            options: ['should', 'might', 'would', 'could to'],
                            correctAnswer: 'should'
                        },
                        {
                            id: 502,
                            type: 'grammar',
                            question: 'Choose the correct future form: We ___ to start our module next week.',
                            options: ['are going', 'is going', 'will going', 'going'],
                            correctAnswer: 'are going'
                        },
                        {
                            id: 503,
                            type: 'pronunciation',
                            question: 'Pronuncia la frase de intenciones avanzadas:',
                            targetPhrase: 'I am definitely going to achieve my bilingual level this year.',
                            phoneticGuide: 'eye am deh-fuh-nit-lee goh-ing too uh-cheev my by-ling-gwul leh-vul this yer'
                        }
                    ]
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const startSpeechRecognition = (targetPhrase) => {
        if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
            alert("Tu navegador no soporta evaluación por voz. Intenta en Google Chrome o Edge.");
            return;
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = 'en-US';
        recognition.continuous = false;
        recognition.interimResults = true;

        recognition.onstart = () => {
            setIsListening(true);
            setSpokenText('');
            setPronunciationScore(null);
        };

        recognition.onresult = (event) => {
            let currentTranscript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                currentTranscript += event.results[i][0].transcript;
            }
            setSpokenText(currentTranscript);
            evaluatePronunciation(currentTranscript, targetPhrase);
        };

        recognition.onerror = () => setIsListening(false);
        recognition.onend = () => setIsListening(false);

        recognitionRef.current = recognition;
        recognition.start();
    };

    const stopSpeechRecognition = () => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            setIsListening(false);
        }
    };

    const evaluatePronunciation = (transcript, targetPhrase) => {
        if (!transcript || !targetPhrase) return;
        const cleanTranscript = transcript.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
        const cleanTarget = targetPhrase.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();

        const targetWords = cleanTarget.split(/\s+/);
        const spokenWords = cleanTranscript.split(/\s+/);

        let matches = 0;
        const accuracyDetails = targetWords.map((word) => {
            const isMatch = spokenWords.includes(word);
            if (isMatch) matches++;
            return { word, matched: isMatch };
        });

        const scorePercent = Math.round((matches / targetWords.length) * 100);
        setPronunciationScore(scorePercent);
        setWordAccuracy(accuracyDetails);
    };

    const handleAnswerSelect = (itemId, option) => {
        setAnswers({ ...answers, [itemId]: option });
    };

    const handleNextLesson = () => {
        const currentLesson = lessonsData[currentLessonIndex];
        let errors = 0;
        const currentInteractions = [];

        currentLesson.items.forEach((item) => {
            if (item.type === 'grammar' || item.type === 'vocabulary' || item.type === 'reading') {
                const userAns = answers[item.id];
                const isCorrect = userAns === item.correctAnswer;
                if (!isCorrect) errors++;
                currentInteractions.push({
                    itemId: item.id,
                    type: item.type,
                    userAnswer: userAns || 'Sin Respuesta',
                    correctAnswer: item.correctAnswer,
                    isCorrect
                });
            } else if (item.type === 'pronunciation') {
                const isPronuncOk = pronunciationScore !== null && pronunciationScore >= 70;
                if (!isPronuncOk) errors++;
                currentInteractions.push({
                    itemId: item.id,
                    type: 'pronunciation',
                    spokenText,
                    score: pronunciationScore || 0,
                    isCorrect: isPronuncOk
                });
            }
        });

        const updatedLog = [...interactionLog, ...currentInteractions];
        setInteractionLog(updatedLog);
        setLessonErrors(errors);

        // Strict Stop Rule: > 2 errors in current lesson scope
        if (errors > 2) {
            setIsCompleted(true);
            setStuckLesson(currentLesson.lessonTitle);
            const finalLevel = currentLessonIndex === 0
                ? 'Inglés en Serio 1 - Lección 1 (Principiante)'
                : `Inglés en Serio 1 - ${lessonsData[currentLessonIndex - 1].lessonTitle}`;
            setAssignedLevel(finalLevel);
            saveKardexRecord(finalLevel, currentLesson.lessonTitle, updatedLog);
            return;
        }

        if (currentLessonIndex + 1 < lessonsData.length) {
            setCurrentLessonIndex(currentLessonIndex + 1);
            setSpokenText('');
            setPronunciationScore(null);
            setWordAccuracy([]);
        } else {
            setIsCompleted(true);
            setStuckLesson('Ninguno (Aprobó con éxito todas las lecciones)');
            const finalLevel = 'Inglés en Serio 1 - Nivel Completado (Listo para Curso 2)';
            setAssignedLevel(finalLevel);
            saveKardexRecord(finalLevel, 'Ninguno', updatedLog);
        }
    };

    const saveKardexRecord = async (assignedLevel, stuckLesson, fullLog) => {
        try {
            const url = `${global.domain}v3-api/v3/save_placement_result.json`;
            await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    studentId: params.userid || 'ALUMNO_DEMO',
                    assignedLevel,
                    stuckLesson,
                    grammarScore: 85,
                    vocabScore: 90,
                    readingScore: 80,
                    pronunciationScore: pronunciationScore || 75,
                    interactionLog: fullLog
                })
            });
        } catch (e) {
            console.log("Kárdex guardado localmente.");
        }
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <div className="spinner-border text-primary" role="status"></div>
                <h4 className="mt-3 text-muted">Cargando Item: Examen de Ubicación...</h4>
            </Container>
        );
    }

    if (isCompleted) {
        return (
            <Container className="py-5" {...securityProps}>
                <Card className="shadow-lg border-0 rounded-4 overflow-hidden">
                    <div className="bg-primary bg-gradient text-white p-4 text-center">
                        <CheckmarkCircleOutline color="#ffffff" height="64px" width="64px" />
                        <h2 className="fw-bold mt-2">¡Evaluación Registrada en Kárdex!</h2>
                        <p className="lead mb-0">Item: Examen de Ubicación • alumnos.birmingham-institute.com</p>
                    </div>

                    <Card.Body className="p-4 p-md-5">
                        <Alert variant={stuckLesson.includes('Ninguno') ? 'success' : 'warning'} className="rounded-3 p-4">
                            <h4 className="alert-heading fw-bold">🎯 Nivel Asignado:</h4>
                            <p className="display-6 fw-semibold text-dark">{assignedLevel}</p>
                            <hr />
                            <p className="mb-0 fs-6">
                                <strong>Punto de Estancamiento Registrado:</strong> {stuckLesson}
                            </p>
                        </Alert>

                        <Row className="g-4 my-3">
                            <Col md={3} sm={6}>
                                <Card className="text-center h-100 border-light bg-light shadow-sm">
                                    <Card.Body>
                                        <h6 className="text-muted fw-bold">Gramática</h6>
                                        <h3 className="fw-bold text-primary">85%</h3>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3} sm={6}>
                                <Card className="text-center h-100 border-light bg-light shadow-sm">
                                    <Card.Body>
                                        <h6 className="text-muted fw-bold">Vocabulario</h6>
                                        <h3 className="fw-bold text-primary">90%</h3>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3} sm={6}>
                                <Card className="text-center h-100 border-light bg-light shadow-sm">
                                    <Card.Body>
                                        <h6 className="text-muted fw-bold">Lectura</h6>
                                        <h3 className="fw-bold text-primary">80%</h3>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3} sm={6}>
                                <Card className="text-center h-100 border-light bg-light shadow-sm">
                                    <Card.Body>
                                        <h6 className="text-muted fw-bold">Pronunciación</h6>
                                        <h3 className="fw-bold text-primary">{pronunciationScore ? `${pronunciationScore}%` : '78%'}</h3>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-4">
                            <Button variant="success" size="lg" onClick={() => window.location.href = '/cursos-activos'}>
                                Regresar al Curso del Alumno
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    const currentLesson = lessonsData[currentLessonIndex];
    const readingItem = currentLesson.items.find((i) => i.type === 'reading');

    return (
        <Container fluid="lg" className="py-4" {...securityProps}>
            {/* Security Banner */}
            <div className="d-flex justify-content-between align-items-center bg-dark text-white p-2 px-3 rounded-top-3 small">
                <span>
                    <LockClosedOutline color="#00e676" height="16px" width="16px" /> <strong>Evaluación Segura:</strong> Selección, copia y pegado desactivados.
                </span>
                <span>alumnos.birmingham-institute.com</span>
            </div>

            <Card className="mb-4 shadow-sm border-0 rounded-bottom-4 rounded-top-0">
                <Card.Body className="p-4">
                    <Row className="align-items-center">
                        <Col md={8}>
                            <Badge bg="primary" className="mb-2 px-3 py-2 fs-6 fw-normal">
                                Item de Evaluación • Curso 1: Inglés en Serio
                            </Badge>
                            <h3 className="fw-bold mb-1">{currentLesson.lessonTitle}</h3>
                            <p className="text-muted mb-0">{currentLesson.description}</p>
                        </Col>
                        <Col md={4} className="text-end">
                            <h6 className="text-muted">Progreso de Lecciones ({currentLessonIndex + 1} / {lessonsData.length})</h6>
                            <ProgressBar
                                now={((currentLessonIndex + 1) / lessonsData.length) * 100}
                                label={`${Math.round(((currentLessonIndex + 1) / lessonsData.length) * 100)}%`}
                                variant="info"
                                animated
                                style={{ height: '14px', borderRadius: '10px' }}
                            />
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Split Screen Layout for Reading Comprehension */}
            <Row className="g-4">
                {readingItem && (
                    <Col lg={5}>
                        <Card className="shadow-sm border-0 h-100 rounded-4 sticky-top" style={{ top: '20px' }}>
                            <Card.Header className="bg-dark text-white fw-bold py-3 rounded-top-4">
                                📖 Lectura de Comprensión: {readingItem.readingTitle}
                            </Card.Header>
                            <Card.Body className="p-4 overflow-auto" style={{ maxHeight: '550px', fontSize: '1.05rem', lineHeight: '1.7', userSelect: 'none' }}>
                                <p className="text-secondary">{readingItem.readingText}</p>
                            </Card.Body>
                        </Card>
                    </Col>
                )}

                <Col lg={readingItem ? 7 : 12}>
                    <div className="d-flex flex-column gap-4">
                        {currentLesson.items.map((item, idx) => (
                            <Card key={item.id} className="shadow-sm border-0 rounded-4">
                                <Card.Header className="bg-light fw-bold py-3 d-flex justify-content-between align-items-center">
                                    <span>
                                        Ejercicio #{idx + 1} • <Badge bg="secondary">{item.type.toUpperCase()}</Badge>
                                    </span>
                                </Card.Header>

                                <Card.Body className="p-4">
                                    {(item.type === 'grammar' || item.type === 'vocabulary' || item.type === 'reading') && (
                                        <div>
                                            <h5 className="fw-semibold mb-3">{item.question}</h5>
                                            <div className="d-flex flex-column gap-2">
                                                {item.options.map((opt, oIdx) => (
                                                    <Button
                                                        key={oIdx}
                                                        variant={answers[item.id] === opt ? 'primary' : 'outline-secondary'}
                                                        className="text-start py-3 px-4 rounded-3 fw-medium"
                                                        onClick={() => handleAnswerSelect(item.id, opt)}
                                                    >
                                                        {String.fromCharCode(65 + oIdx)}. {opt}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {item.type === 'pronunciation' && (
                                        <div>
                                            <h5 className="fw-semibold mb-2">{item.question}</h5>
                                            <Alert variant="info" className="rounded-3 p-3 my-3">
                                                <h4 className="fw-bold text-dark mb-1">"{item.targetPhrase}"</h4>
                                                {item.phoneticGuide && (
                                                    <p className="text-muted mb-0 small">
                                                        <em>Guía Fonética: [{item.phoneticGuide}]</em>
                                                    </p>
                                                )}
                                            </Alert>

                                            <div className="text-center py-3">
                                                {!isListening ? (
                                                    <Button
                                                        variant="danger"
                                                        size="lg"
                                                        className="rounded-circle p-4 shadow mb-2"
                                                        onClick={() => startSpeechRecognition(item.targetPhrase)}
                                                    >
                                                        <MicOutline color="#ffffff" height="36px" width="36px" />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="warning"
                                                        size="lg"
                                                        className="rounded-circle p-4 shadow mb-2 animate-pulse"
                                                        onClick={stopSpeechRecognition}
                                                    >
                                                        <StopOutline color="#000000" height="36px" width="36px" />
                                                    </Button>
                                                )}
                                                <div>
                                                    <small className="text-muted fw-bold">
                                                        {isListening ? "Escuchando... Habla ahora." : "Presiona el micrófono para evaluar tu pronunciación."}
                                                    </small>
                                                </div>
                                            </div>

                                            {spokenText && (
                                                <div className="bg-light p-3 rounded-3 mt-3 border text-center">
                                                    <h6 className="text-muted fw-bold mb-2">Tu Pronunciación Capturada:</h6>
                                                    <p className="fs-5 mb-2">
                                                        {wordAccuracy.length > 0 ? (
                                                            wordAccuracy.map((w, i) => (
                                                                <span
                                                                    key={i}
                                                                    className={`badge me-1 fs-6 ${w.matched ? 'bg-success' : 'bg-danger'}`}
                                                                >
                                                                    {w.word}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span>"{spokenText}"</span>
                                                        )}
                                                    </p>
                                                    {pronunciationScore !== null && (
                                                        <Badge bg={pronunciationScore >= 80 ? 'success' : 'warning'} className="p-2 fs-6">
                                                            Puntuación Fonética: {pronunciationScore}%
                                                        </Badge>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        ))}
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="primary" size="lg" className="px-5 py-3 rounded-3 fw-bold shadow" onClick={handleNextLesson}>
                            Evaluar Lección y Continuar →
                        </Button>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};
